import type PageDataContext from "@core/Context/PageDataContext";
import Diagrams from "@core/components/Diagram/Diagrams";
import type DiagramType from "@core/components/Diagram/DiagramType";
import type { ResourceServiceType } from "@core-ui/ContextServices/ResourceService/ResourceService";
import { getDiagramDescriptor } from "@ext/markdown/elements/diagrams/logic/diagramDescriptors";
import getNaturalSize from "@ext/markdown/elements/diagrams/logic/getNaturalSize";
import type { Editor } from "@tiptap/core";

const createDiagramFile = async (
	editor: Editor,
	fileName: string,
	resourceService: ResourceServiceType,
	diagramName: DiagramType,
	pageDataContext: PageDataContext,
	content: string,
) => {
	const name = `${fileName}.${getDiagramDescriptor(diagramName).extension}`;
	const newName = await resourceService.setResource(name, content);

	if (!newName) return;

	const attributes: { src: string; diagramName: string; width?: string; height?: string } = {
		src: newName,
		diagramName,
	};
	try {
		const diagramsServiceUrl = pageDataContext.settings?.services?.["diagram-renderer"]?.endpoint;
		const newSize = getNaturalSize(await new Diagrams(diagramsServiceUrl).getDiagram(diagramName, content));

		if (newSize?.width && newSize?.height) {
			attributes.width = `${newSize.width}px`;
			attributes.height = `${newSize.height}px`;
		}
	} catch (error) {
		console.error("Error creating diagram:", error);
	}

	editor.chain().setDiagrams(attributes).run();
};

// A file costs a second entry in the catalog and in the publish diff; a dropped file stays a file.
const createDiagrams = async (
	editor: Editor,
	fileName: string,
	resourceService: ResourceServiceType,
	diagramName: DiagramType,
	pageDataContext: PageDataContext,
	diagramContent?: string,
) => {
	const { template, inlineFence } = getDiagramDescriptor(diagramName);

	if (inlineFence && diagramContent === undefined) {
		editor.chain().setDiagrams({ content: template, diagramName }).run();
		return;
	}

	await createDiagramFile(
		editor,
		fileName,
		resourceService,
		diagramName,
		pageDataContext,
		diagramContent ?? template,
	);
};

export default createDiagrams;
